import {useSessionEvents} from '../../hooks/use-session-events'
import {useListCommands} from '../../hooks/useListCommands'
import {useAgentProfile} from '../../contexts/agent-profile-context'
import {useOptimisticLogsOptional} from '../../contexts/OptimisticLogsContext'
import {isTurn} from '../../types/events'
import {LoadingMessage} from './LoadingMessage'
import {TurnView} from './events/TurnView'
import {Abort} from './events/Abort'
import styles from './EventSessionLogs.module.css'

export interface EventSessionLogsProps {
  /** The task ID to fetch events for */
  taskId: string | undefined
  /** Polling interval in ms (fallback when Alive is not connected) */
  pollingInterval?: number
}

/**
 * Session logs component powered by the events API.
 * Fetches raw SessionEvents via Alive push + cursor-based incremental loading,
 * builds a ConversationModel, and renders the timeline.
 */
export function EventSessionLogs({taskId, pollingInterval = 3000}: EventSessionLogsProps) {
  const {data: commands} = useListCommands({pollingInterval})
  const {data, isLoading, error} = useSessionEvents({taskId, pollingInterval, enabled: true, commands})
  const agentProfile = useAgentProfile()
  const optimisticLogs = useOptimisticLogsOptional()
  const model = data?.model

  const EmptyStateComponent = agentProfile.slots.EmptyState
  const isEmptySession = !!model && !model.hasVisibleContent && !model.isWorking && !!EmptyStateComponent

  // Show thinking indicator when the agent is working OR when the user has
  // responded to a prompt but the server hasn't confirmed yet (optimistic gap).
  const hasUnreconciledOptimistic = (optimisticLogs?.optimisticResponses.length ?? 0) > 0
  const showThinking = model && (model.isWorking || hasUnreconciledOptimistic)

  if (isLoading && !model) {
    return <LoadingMessage message="Loading…" />
  }

  if (error && !model) {
    return <div>Error loading session events.</div>
  }

  if (!model) {
    return <div>No events to display.</div>
  }

  if (isEmptySession && EmptyStateComponent) {
    return <EmptyStateComponent />
  }

  return (
    <div data-testid="event-session-logs" className={styles.container}>
      {!model.hasVisibleContent && !model.isWorking && <div>No events to display.</div>}
      {model.items.map((item, index) => {
        if (isTurn(item)) {
          return (
            <TurnView
              key={item.turnId ?? `turn-${index}`}
              turn={item}
              mcpToolNameMappings={model.mcpToolNameMappings}
            />
          )
        }

        return <Abort key={item.id} item={item} />
      })}
      {showThinking && <LoadingMessage message={model.currentIntent || 'Thinking…'} />}
    </div>
  )
}
